import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Leaf } from 'lucide-react';
import { Button } from '@/components/ui/button';

const HERO_BG = "https://images.unsplash.com/photo-1601655856683-8400aa9ef59f?w=1920&q=80";

// Maple leaf SVG component - authentic maple leaf shape
const MapleLeaf = ({ className, style }) => (
  <svg 
    viewBox="0 0 100 100" 
    className={className}
    style={style}
    fill="currentColor"
  >
    <path d="M50 97 L50 60 M50 60 C45 55 30 58 25 50 C30 50 35 45 35 40 C25 42 15 38 10 30 C20 32 25 28 30 22 C25 20 22 12 25 5 C32 12 40 15 50 10 C60 15 68 12 75 5 C78 12 75 20 70 22 C75 28 80 32 90 30 C85 38 75 42 65 40 C65 45 70 50 75 50 C70 58 55 55 50 60"/>
  </svg>
);

// Line art maple tree SVG
const MapleTree = () => (
  <svg 
    viewBox="0 0 400 700" 
    className="absolute right-0 bottom-0 h-full w-auto opacity-40"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {/* Main trunk */}
    <path d="M200 700 L200 400" className="text-brand-brown" strokeWidth="3"/>
    <path d="M200 400 L200 250" className="text-brand-brown" strokeWidth="2.5"/>
    <path d="M200 250 L200 150" className="text-brand-brown" strokeWidth="2"/>
    
    {/* Main branches - left side */}
    <path d="M200 380 Q150 350 100 320" className="text-brand-brown" strokeWidth="2"/>
    <path d="M100 320 Q70 300 50 260" className="text-brand-brown" strokeWidth="1.5"/>
    <path d="M100 320 Q80 340 60 350" className="text-brand-brown" strokeWidth="1"/>
    
    <path d="M200 320 Q140 280 90 240" className="text-brand-brown" strokeWidth="1.5"/>
    <path d="M90 240 Q60 220 40 180" className="text-brand-brown" strokeWidth="1"/>
    <path d="M90 240 Q70 260 50 280" className="text-brand-brown" strokeWidth="1"/>
    
    <path d="M200 260 Q150 220 110 180" className="text-brand-brown" strokeWidth="1.5"/>
    <path d="M110 180 Q90 150 80 120" className="text-brand-brown" strokeWidth="1"/>
    
    <path d="M200 200 Q160 170 130 130" className="text-brand-brown" strokeWidth="1"/>
    <path d="M130 130 Q110 100 100 70" className="text-brand-brown" strokeWidth="0.8"/>
    
    {/* Main branches - right side */}
    <path d="M200 380 Q250 350 300 320" className="text-brand-brown" strokeWidth="2"/>
    <path d="M300 320 Q330 300 350 260" className="text-brand-brown" strokeWidth="1.5"/>
    <path d="M300 320 Q320 340 340 350" className="text-brand-brown" strokeWidth="1"/>
    
    <path d="M200 320 Q260 280 310 240" className="text-brand-brown" strokeWidth="1.5"/>
    <path d="M310 240 Q340 220 360 180" className="text-brand-brown" strokeWidth="1"/>
    <path d="M310 240 Q330 260 350 280" className="text-brand-brown" strokeWidth="1"/>
    
    <path d="M200 260 Q250 220 290 180" className="text-brand-brown" strokeWidth="1.5"/>
    <path d="M290 180 Q310 150 320 120" className="text-brand-brown" strokeWidth="1"/>
    
    <path d="M200 200 Q240 170 270 130" className="text-brand-brown" strokeWidth="1"/>
    <path d="M270 130 Q290 100 300 70" className="text-brand-brown" strokeWidth="0.8"/>
    
    {/* Top branches */}
    <path d="M200 150 Q170 120 150 80" className="text-brand-brown" strokeWidth="1"/>
    <path d="M200 150 Q230 120 250 80" className="text-brand-brown" strokeWidth="1"/>
    <path d="M200 150 Q200 100 200 50" className="text-brand-brown" strokeWidth="1"/>
    
    {/* Small twigs */}
    <path d="M50 260 Q30 240 20 210" className="text-brand-brown" strokeWidth="0.5"/>
    <path d="M350 260 Q370 240 380 210" className="text-brand-brown" strokeWidth="0.5"/>
    <path d="M40 180 Q20 160 10 130" className="text-brand-brown" strokeWidth="0.5"/>
    <path d="M360 180 Q380 160 390 130" className="text-brand-brown" strokeWidth="0.5"/>
    
    {/* Maple leaves on branches - using maple leaf paths */}
    <g className="text-brand-green" fill="currentColor" stroke="none" opacity="0.6">
      {/* Left side leaves */}
      <path transform="translate(35, 245) scale(0.35)" d="M50 97 L50 60 M50 60 C45 55 30 58 25 50 C30 50 35 45 35 40 C25 42 15 38 10 30 C20 32 25 28 30 22 C25 20 22 12 25 5 C32 12 40 15 50 10 C60 15 68 12 75 5 C78 12 75 20 70 22 C75 28 80 32 90 30 C85 38 75 42 65 40 C65 45 70 50 75 50 C70 58 55 55 50 60"/>
      <path transform="translate(10, 190) scale(0.30)" d="M50 97 L50 60 M50 60 C45 55 30 58 25 50 C30 50 35 45 35 40 C25 42 15 38 10 30 C20 32 25 28 30 22 C25 20 22 12 25 5 C32 12 40 15 50 10 C60 15 68 12 75 5 C78 12 75 20 70 22 C75 28 80 32 90 30 C85 38 75 42 65 40 C65 45 70 50 75 50 C70 58 55 55 50 60"/>
      <path transform="translate(45, 330) scale(0.32)" d="M50 97 L50 60 M50 60 C45 55 30 58 25 50 C30 50 35 45 35 40 C25 42 15 38 10 30 C20 32 25 28 30 22 C25 20 22 12 25 5 C32 12 40 15 50 10 C60 15 68 12 75 5 C78 12 75 20 70 22 C75 28 80 32 90 30 C85 38 75 42 65 40 C65 45 70 50 75 50 C70 58 55 55 50 60"/>
      <path transform="translate(20, 155) scale(0.38)" d="M50 97 L50 60 M50 60 C45 55 30 58 25 50 C30 50 35 45 35 40 C25 42 15 38 10 30 C20 32 25 28 30 22 C25 20 22 12 25 5 C32 12 40 15 50 10 C60 15 68 12 75 5 C78 12 75 20 70 22 C75 28 80 32 90 30 C85 38 75 42 65 40 C65 45 70 50 75 50 C70 58 55 55 50 60"/>
      <path transform="translate(60, 100) scale(0.28)" d="M50 97 L50 60 M50 60 C45 55 30 58 25 50 C30 50 35 45 35 40 C25 42 15 38 10 30 C20 32 25 28 30 22 C25 20 22 12 25 5 C32 12 40 15 50 10 C60 15 68 12 75 5 C78 12 75 20 70 22 C75 28 80 32 90 30 C85 38 75 42 65 40 C65 45 70 50 75 50 C70 58 55 55 50 60"/>
      <path transform="translate(85, 50) scale(0.25)" d="M50 97 L50 60 M50 60 C45 55 30 58 25 50 C30 50 35 45 35 40 C25 42 15 38 10 30 C20 32 25 28 30 22 C25 20 22 12 25 5 C32 12 40 15 50 10 C60 15 68 12 75 5 C78 12 75 20 70 22 C75 28 80 32 90 30 C85 38 75 42 65 40 C65 45 70 50 75 50 C70 58 55 55 50 60"/>
      <path transform="translate(70, 220) scale(0.33)" d="M50 97 L50 60 M50 60 C45 55 30 58 25 50 C30 50 35 45 35 40 C25 42 15 38 10 30 C20 32 25 28 30 22 C25 20 22 12 25 5 C32 12 40 15 50 10 C60 15 68 12 75 5 C78 12 75 20 70 22 C75 28 80 32 90 30 C85 38 75 42 65 40 C65 45 70 50 75 50 C70 58 55 55 50 60"/>
      <path transform="translate(30, 265) scale(0.28)" d="M50 97 L50 60 M50 60 C45 55 30 58 25 50 C30 50 35 45 35 40 C25 42 15 38 10 30 C20 32 25 28 30 22 C25 20 22 12 25 5 C32 12 40 15 50 10 C60 15 68 12 75 5 C78 12 75 20 70 22 C75 28 80 32 90 30 C85 38 75 42 65 40 C65 45 70 50 75 50 C70 58 55 55 50 60"/>
      <path transform="translate(95, 160) scale(0.32)" d="M50 97 L50 60 M50 60 C45 55 30 58 25 50 C30 50 35 45 35 40 C25 42 15 38 10 30 C20 32 25 28 30 22 C25 20 22 12 25 5 C32 12 40 15 50 10 C60 15 68 12 75 5 C78 12 75 20 70 22 C75 28 80 32 90 30 C85 38 75 42 65 40 C65 45 70 50 75 50 C70 58 55 55 50 60"/>
      <path transform="translate(115, 110) scale(0.28)" d="M50 97 L50 60 M50 60 C45 55 30 58 25 50 C30 50 35 45 35 40 C25 42 15 38 10 30 C20 32 25 28 30 22 C25 20 22 12 25 5 C32 12 40 15 50 10 C60 15 68 12 75 5 C78 12 75 20 70 22 C75 28 80 32 90 30 C85 38 75 42 65 40 C65 45 70 50 75 50 C70 58 55 55 50 60"/>
      <path transform="translate(135, 60) scale(0.32)" d="M50 97 L50 60 M50 60 C45 55 30 58 25 50 C30 50 35 45 35 40 C25 42 15 38 10 30 C20 32 25 28 30 22 C25 20 22 12 25 5 C32 12 40 15 50 10 C60 15 68 12 75 5 C78 12 75 20 70 22 C75 28 80 32 90 30 C85 38 75 42 65 40 C65 45 70 50 75 50 C70 58 55 55 50 60"/>
      
      {/* Right side leaves */}
      <path transform="translate(330, 245) scale(0.35)" d="M50 97 L50 60 M50 60 C45 55 30 58 25 50 C30 50 35 45 35 40 C25 42 15 38 10 30 C20 32 25 28 30 22 C25 20 22 12 25 5 C32 12 40 15 50 10 C60 15 68 12 75 5 C78 12 75 20 70 22 C75 28 80 32 90 30 C85 38 75 42 65 40 C65 45 70 50 75 50 C70 58 55 55 50 60"/>
      <path transform="translate(355, 190) scale(0.30)" d="M50 97 L50 60 M50 60 C45 55 30 58 25 50 C30 50 35 45 35 40 C25 42 15 38 10 30 C20 32 25 28 30 22 C25 20 22 12 25 5 C32 12 40 15 50 10 C60 15 68 12 75 5 C78 12 75 20 70 22 C75 28 80 32 90 30 C85 38 75 42 65 40 C65 45 70 50 75 50 C70 58 55 55 50 60"/>
      <path transform="translate(320, 330) scale(0.32)" d="M50 97 L50 60 M50 60 C45 55 30 58 25 50 C30 50 35 45 35 40 C25 42 15 38 10 30 C20 32 25 28 30 22 C25 20 22 12 25 5 C32 12 40 15 50 10 C60 15 68 12 75 5 C78 12 75 20 70 22 C75 28 80 32 90 30 C85 38 75 42 65 40 C65 45 70 50 75 50 C70 58 55 55 50 60"/>
      <path transform="translate(340, 155) scale(0.38)" d="M50 97 L50 60 M50 60 C45 55 30 58 25 50 C30 50 35 45 35 40 C25 42 15 38 10 30 C20 32 25 28 30 22 C25 20 22 12 25 5 C32 12 40 15 50 10 C60 15 68 12 75 5 C78 12 75 20 70 22 C75 28 80 32 90 30 C85 38 75 42 65 40 C65 45 70 50 75 50 C70 58 55 55 50 60"/>
      <path transform="translate(300, 100) scale(0.28)" d="M50 97 L50 60 M50 60 C45 55 30 58 25 50 C30 50 35 45 35 40 C25 42 15 38 10 30 C20 32 25 28 30 22 C25 20 22 12 25 5 C32 12 40 15 50 10 C60 15 68 12 75 5 C78 12 75 20 70 22 C75 28 80 32 90 30 C85 38 75 42 65 40 C65 45 70 50 75 50 C70 58 55 55 50 60"/>
      <path transform="translate(280, 50) scale(0.25)" d="M50 97 L50 60 M50 60 C45 55 30 58 25 50 C30 50 35 45 35 40 C25 42 15 38 10 30 C20 32 25 28 30 22 C25 20 22 12 25 5 C32 12 40 15 50 10 C60 15 68 12 75 5 C78 12 75 20 70 22 C75 28 80 32 90 30 C85 38 75 42 65 40 C65 45 70 50 75 50 C70 58 55 55 50 60"/>
      <path transform="translate(290, 220) scale(0.33)" d="M50 97 L50 60 M50 60 C45 55 30 58 25 50 C30 50 35 45 35 40 C25 42 15 38 10 30 C20 32 25 28 30 22 C25 20 22 12 25 5 C32 12 40 15 50 10 C60 15 68 12 75 5 C78 12 75 20 70 22 C75 28 80 32 90 30 C85 38 75 42 65 40 C65 45 70 50 75 50 C70 58 55 55 50 60"/>
      <path transform="translate(335, 265) scale(0.28)" d="M50 97 L50 60 M50 60 C45 55 30 58 25 50 C30 50 35 45 35 40 C25 42 15 38 10 30 C20 32 25 28 30 22 C25 20 22 12 25 5 C32 12 40 15 50 10 C60 15 68 12 75 5 C78 12 75 20 70 22 C75 28 80 32 90 30 C85 38 75 42 65 40 C65 45 70 50 75 50 C70 58 55 55 50 60"/>
      <path transform="translate(270, 160) scale(0.32)" d="M50 97 L50 60 M50 60 C45 55 30 58 25 50 C30 50 35 45 35 40 C25 42 15 38 10 30 C20 32 25 28 30 22 C25 20 22 12 25 5 C32 12 40 15 50 10 C60 15 68 12 75 5 C78 12 75 20 70 22 C75 28 80 32 90 30 C85 38 75 42 65 40 C65 45 70 50 75 50 C70 58 55 55 50 60"/>
      <path transform="translate(250, 110) scale(0.28)" d="M50 97 L50 60 M50 60 C45 55 30 58 25 50 C30 50 35 45 35 40 C25 42 15 38 10 30 C20 32 25 28 30 22 C25 20 22 12 25 5 C32 12 40 15 50 10 C60 15 68 12 75 5 C78 12 75 20 70 22 C75 28 80 32 90 30 C85 38 75 42 65 40 C65 45 70 50 75 50 C70 58 55 55 50 60"/>
      <path transform="translate(230, 60) scale(0.32)" d="M50 97 L50 60 M50 60 C45 55 30 58 25 50 C30 50 35 45 35 40 C25 42 15 38 10 30 C20 32 25 28 30 22 C25 20 22 12 25 5 C32 12 40 15 50 10 C60 15 68 12 75 5 C78 12 75 20 70 22 C75 28 80 32 90 30 C85 38 75 42 65 40 C65 45 70 50 75 50 C70 58 55 55 50 60"/>
      
      {/* Top leaves */}
      <path transform="translate(185, 30) scale(0.30)" d="M50 97 L50 60 M50 60 C45 55 30 58 25 50 C30 50 35 45 35 40 C25 42 15 38 10 30 C20 32 25 28 30 22 C25 20 22 12 25 5 C32 12 40 15 50 10 C60 15 68 12 75 5 C78 12 75 20 70 22 C75 28 80 32 90 30 C85 38 75 42 65 40 C65 45 70 50 75 50 C70 58 55 55 50 60"/>
      <path transform="translate(160, 70) scale(0.25)" d="M50 97 L50 60 M50 60 C45 55 30 58 25 50 C30 50 35 45 35 40 C25 42 15 38 10 30 C20 32 25 28 30 22 C25 20 22 12 25 5 C32 12 40 15 50 10 C60 15 68 12 75 5 C78 12 75 20 70 22 C75 28 80 32 90 30 C85 38 75 42 65 40 C65 45 70 50 75 50 C70 58 55 55 50 60"/>
      <path transform="translate(205, 70) scale(0.25)" d="M50 97 L50 60 M50 60 C45 55 30 58 25 50 C30 50 35 45 35 40 C25 42 15 38 10 30 C20 32 25 28 30 22 C25 20 22 12 25 5 C32 12 40 15 50 10 C60 15 68 12 75 5 C78 12 75 20 70 22 C75 28 80 32 90 30 C85 38 75 42 65 40 C65 45 70 50 75 50 C70 58 55 55 50 60"/>
    </g>
    
    {/* Brown leaves mixed in */}
    <g className="text-brand-brown" fill="currentColor" stroke="none" opacity="0.5">
      <path transform="translate(55, 285) scale(0.25)" d="M50 97 L50 60 M50 60 C45 55 30 58 25 50 C30 50 35 45 35 40 C25 42 15 38 10 30 C20 32 25 28 30 22 C25 20 22 12 25 5 C32 12 40 15 50 10 C60 15 68 12 75 5 C78 12 75 20 70 22 C75 28 80 32 90 30 C85 38 75 42 65 40 C65 45 70 50 75 50 C70 58 55 55 50 60"/>
      <path transform="translate(315, 285) scale(0.25)" d="M50 97 L50 60 M50 60 C45 55 30 58 25 50 C30 50 35 45 35 40 C25 42 15 38 10 30 C20 32 25 28 30 22 C25 20 22 12 25 5 C32 12 40 15 50 10 C60 15 68 12 75 5 C78 12 75 20 70 22 C75 28 80 32 90 30 C85 38 75 42 65 40 C65 45 70 50 75 50 C70 58 55 55 50 60"/>
      <path transform="translate(40, 215) scale(0.22)" d="M50 97 L50 60 M50 60 C45 55 30 58 25 50 C30 50 35 45 35 40 C25 42 15 38 10 30 C20 32 25 28 30 22 C25 20 22 12 25 5 C32 12 40 15 50 10 C60 15 68 12 75 5 C78 12 75 20 70 22 C75 28 80 32 90 30 C85 38 75 42 65 40 C65 45 70 50 75 50 C70 58 55 55 50 60"/>
      <path transform="translate(330, 215) scale(0.22)" d="M50 97 L50 60 M50 60 C45 55 30 58 25 50 C30 50 35 45 35 40 C25 42 15 38 10 30 C20 32 25 28 30 22 C25 20 22 12 25 5 C32 12 40 15 50 10 C60 15 68 12 75 5 C78 12 75 20 70 22 C75 28 80 32 90 30 C85 38 75 42 65 40 C65 45 70 50 75 50 C70 58 55 55 50 60"/>
      <path transform="translate(105, 145) scale(0.28)" d="M50 97 L50 60 M50 60 C45 55 30 58 25 50 C30 50 35 45 35 40 C25 42 15 38 10 30 C20 32 25 28 30 22 C25 20 22 12 25 5 C32 12 40 15 50 10 C60 15 68 12 75 5 C78 12 75 20 70 22 C75 28 80 32 90 30 C85 38 75 42 65 40 C65 45 70 50 75 50 C70 58 55 55 50 60"/>
      <path transform="translate(265, 145) scale(0.28)" d="M50 97 L50 60 M50 60 C45 55 30 58 25 50 C30 50 35 45 35 40 C25 42 15 38 10 30 C20 32 25 28 30 22 C25 20 22 12 25 5 C32 12 40 15 50 10 C60 15 68 12 75 5 C78 12 75 20 70 22 C75 28 80 32 90 30 C85 38 75 42 65 40 C65 45 70 50 75 50 C70 58 55 55 50 60"/>
    </g>
  </svg>
);

// Falling leaves data - originating from tree position (right side)
const fallingLeaves = [
  { id: 1, size: 55, left: '75%', delay: 0, duration: 12, color: '#1a5c1e' },
  { id: 2, size: 44, left: '80%', delay: 2, duration: 15, color: '#a6533a' },
  { id: 3, size: 50, left: '70%', delay: 4, duration: 13, color: '#2d5a2d' },
  { id: 4, size: 40, left: '85%', delay: 1, duration: 16, color: '#8b6b4a' },
  { id: 5, size: 58, left: '65%', delay: 3, duration: 14, color: '#1a5c1e' },
  { id: 6, size: 38, left: '90%', delay: 5, duration: 17, color: '#a6533a' },
  { id: 7, size: 48, left: '72%', delay: 6, duration: 13, color: '#6b5344' },
  { id: 8, size: 42, left: '78%', delay: 7, duration: 15, color: '#3d5a3d' },
  { id: 9, size: 52, left: '68%', delay: 8, duration: 14, color: '#1a5c1e' },
  { id: 10, size: 38, left: '82%', delay: 9, duration: 16, color: '#a6533a' },
  { id: 11, size: 46, left: '60%', delay: 3, duration: 18, color: '#2d5a2d' },
  { id: 12, size: 36, left: '88%', delay: 4, duration: 15, color: '#8b6b4a' },
];

export const Hero = () => {
  return (
    <section 
      data-testid="hero-section"
      className="relative min-h-screen flex items-center pt-20 overflow-hidden"
    >
      {/* Full Background - Abstract Green/Brown Art */}
      <div className="absolute inset-0 z-0">
        <img 
          src={HERO_BG}
          alt="Abstract green and brown canvas art"
          className="w-full h-full object-cover"
          style={{ filter: 'saturate(0.6) sepia(0.2) brightness(0.95)' }}
        />
        {/* Natural earth tone overlay matching logo theme */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#3d5a3d]/25 via-transparent to-[#6b5344]/25"></div>
        {/* Overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/88 to-white/50"></div>
      </div>

      {/* Line Art Maple Tree - Right Side */}
      <div className="absolute inset-0 z-[1] pointer-events-none overflow-hidden">
        <MapleTree />
      </div>

      {/* Falling Maple Leaves - from tree */}
      <div className="absolute inset-0 z-[2] overflow-hidden pointer-events-none">
        {fallingLeaves.map((leaf) => (
          <motion.div
            key={leaf.id}
            className="absolute"
            style={{ left: leaf.left, top: -50 }}
            initial={{ y: -50, rotate: 0, opacity: 0 }}
            animate={{ 
              y: ['0vh', '110vh'],
              rotate: [0, 360, 180, 360],
              opacity: [0, 0.5, 0.5, 0]
            }}
            transition={{
              duration: leaf.duration,
              delay: leaf.delay,
              repeat: Infinity,
              ease: "linear"
            }}
          >
            <motion.div
              animate={{ 
                x: [0, -40, 20, -30, 0],
                rotateY: [0, 180, 360]
              }}
              transition={{
                duration: leaf.duration / 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <MapleLeaf 
                className="drop-shadow-sm"
                style={{ 
                  width: leaf.size, 
                  height: leaf.size,
                  color: leaf.color
                }} 
              />
            </motion.div>
          </motion.div>
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 w-full px-8 md:px-16 lg:px-24 py-20 md:py-32">
        <div className="max-w-2xl">
          {/* Main Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-serif text-5xl sm:text-6xl lg:text-7xl xl:text-8xl text-brand-dark leading-[1.05] mb-8 text-left"
          >
            Navigating <span className="gradient-text">Tomorrow,</span><br />
            <span className="text-brand-dark">Transforming</span> <span className="gradient-text">Today</span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="font-serif text-base md:text-lg lg:text-xl text-gray-700 max-w-xl mb-10 leading-relaxed text-left"
          >
            The next decade of business will be shaped by climate, capital, and credibility. 
            We help organisations get ahead of all three.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-wrap gap-4 justify-start"
          >
            <Link to="/contact">
              <Button 
                data-testid="hero-cta-primary"
                className="btn-gradient text-white font-sans font-medium px-6 py-5 text-sm rounded-full shadow-lg"
              >
                Start Your Journey
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/services">
              <Button 
                variant="outline"
                data-testid="hero-cta-secondary"
                className="font-sans font-medium px-6 py-5 text-sm border-2 border-brand-dark text-brand-dark hover:bg-brand-dark hover:text-white rounded-full backdrop-blur-sm bg-white/50"
              >
                Explore Services
              </Button>
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
